import {gql} from '@apollo/client';
import {createStyles, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getAgentFactoryDescription} from 'llm/chat-library/factories/AgentFactoriesDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  AgentFactoryQuery as AgentFactoryQueryData,
  AgentFactoryQueryVariables,
} from './__generated/AgentFactoryQuery';

const useStyles = createStyles((theme) => ({
  container: {
    padding: theme.spacing.xl,
  },
}));

export const AGENT_FACTORY_QUERY = gql`
  query AgentFactoryQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentFactory(id: $id) {
      __typename
      ... on AgentFactoryNode {
        id
        name
        configuration
        createdAt
        creatorName
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Detail view for a single Agent Factory, rendered in the sidepanel's main content slot (the same
 * slot the chat occupies) and addressable at its own URL (see AIRoutes). Deliberately a placeholder
 * for now: it renders only the agent factory's name and its description (read out of `configuration`,
 * since the domain model has no top-level description field yet) -- no canvas, agent list, or tracker.
 */
export default function AgentFactoryPage({agentFactoryId}: {agentFactoryId: string}): JSX.Element {
  const {classes} = useStyles();
  const queryResult = useBenchlingQuery<AgentFactoryQueryData, AgentFactoryQueryVariables>(
    AGENT_FACTORY_QUERY,
    {variables: {id: agentFactoryId}}
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResult.type === QueryResultType.ERROR ||
    queryResult.data.agentFactory == null ||
    isInaccessible(queryResult.data.agentFactory)
  ) {
    return (
      <Stack spacing="md" className={classes.container}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="This factory could not be found, or you do not have access to it."
        />
      </Stack>
    );
  }

  const {agentFactory} = queryResult.data;

  return (
    <Stack spacing="md" className={classes.container}>
      <Title order={3}>{agentFactory.name}</Title>
      <Text c="dimmed">
        {getAgentFactoryDescription(agentFactory.configuration) || 'No description yet.'}
      </Text>
    </Stack>
  );
}
